/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import LoggerFactory from '../../logger/LoggerFactory';
import IDisposable from '../../lang/IDisposable';
import ReadOnlySubject from '../../rx/ReadOnlySubject';
import ExponentialBackoff from '../../time/ExponentialBackoff';
import EndPoint, {IStream} from '../discovery/EndPoint';
import SDK from '../SDK';
import GarbageCollectorManager from '../../dom/GarbageCollectorManager';
import IPeerConnection from '../../rtc/IPeerConnection';
import {IRtcPublishMonitorStatistic} from '../../rtc/RtcPublishMonitor';
import PublisherState from './PublisherState';
import assertUnreachable from '../../lang/assertUnreachable';
import Disposable from '../../lang/Disposable';
import {ILogger} from '../../logger/LoggerInterface';
import SessionTelemetry from '../../video/SessionTelemetry';
import EdgeAuthParser from '../../edgeAuth/EdgeAuthParser';

import {EncodedEdgeToken} from '../../edgeAuth/EncodedEdgeToken';
import TokenContext from '../context/TokenContext';
import PublisherContext from './PublisherContext';
import PeerConnectionContext from '../context/PeerConnectionContext';
import StateContext from '../context/StateContext';
import Discovery from '../discovery/Discovery';
import DiscoveryUri from '../discovery/DiscoveryUri';
import MetricsFactory from '../../metrics/MetricsFactory';
import MetricsService from '../../metrics/MetricsService';
import RealTimePublisher from './RealTimePublisher';
import FeatureEnablement from '../../environment/FeatureEnablement';

const defaultStreamTerminationReason = 'client:termination';
const failureCountCleanUpIntervalInMilliseconds = 3000;
const standbyPollingIntervalInMilliseconds = 15000;
const oneHourInMilliseconds = 3600 * 1000;
const maxReconnectingAttemptsIntervalInMilliseconds = 2 * oneHourInMilliseconds;

export default class Publisher implements IDisposable {
  private readonly _logger: ILogger = LoggerFactory.getLogger('Publisher');
  private readonly _tokenContext: TokenContext;
  private readonly _publisherContext: PublisherContext;
  private readonly _peerConnectionContext: PeerConnectionContext;
  private readonly _stateContext: StateContext;
  private readonly _exponentialBackoff: ExponentialBackoff;
  private readonly _publisherStartTime: number;
  private readonly _readOnlyToken: ReadOnlySubject<EncodedEdgeToken>;
  private readonly _readOnlyPeerConnection: ReadOnlySubject<IPeerConnection | null>;
  private readonly _readOnlyState: ReadOnlySubject<PublisherState>;
  private readonly _readOnlyTokenExpiring: ReadOnlySubject<boolean>;
  private readonly _readOnlyAuthorized: ReadOnlySubject<boolean>;
  private readonly _readOnlyOnline: ReadOnlySubject<boolean>;
  private readonly _readOnlyLoading: ReadOnlySubject<boolean>;
  private readonly _readOnlyPublishing: ReadOnlySubject<boolean>;
  private readonly _readOnlyStandby: ReadOnlySubject<boolean>;
  private readonly _readOnlyStopped: ReadOnlySubject<boolean>;
  private readonly _readOnlyFailureCount: ReadOnlySubject<number>;
  private readonly _readOnlyEndPoint: ReadOnlySubject<EndPoint | null>;
  private readonly _readOnlyStream: ReadOnlySubject<IStream | null>;
  private readonly _readOnlyRtcStatistics: ReadOnlySubject<IRtcPublishMonitorStatistic | null>;
  private readonly _readOnlyMediaStream: ReadOnlySubject<MediaStream | null>;

  private _metricsService: MetricsService;
  private readonly _sessionTelemetry: SessionTelemetry;

  constructor(mediaStream: MediaStream, token: EncodedEdgeToken, name?: string) {
    this._tokenContext = new TokenContext(token);
    this._publisherContext = new PublisherContext(name);
    this._peerConnectionContext = new PeerConnectionContext();
    this._stateContext = new StateContext();
    this._exponentialBackoff = new ExponentialBackoff();
    this._publisherStartTime = Date.now();
    this._readOnlyToken = new ReadOnlySubject<string>(this._tokenContext.token);
    this._readOnlyPeerConnection = new ReadOnlySubject<IPeerConnection | null>(this._peerConnectionContext.peerConnection);
    this._readOnlyState = new ReadOnlySubject<PublisherState>(this._publisherContext.state);
    this._readOnlyTokenExpiring = new ReadOnlySubject<boolean>(this._tokenContext.tokenExpiring);
    this._readOnlyAuthorized = new ReadOnlySubject<boolean>(this._publisherContext.authorized);
    this._readOnlyOnline = new ReadOnlySubject<boolean>(this._publisherContext.online);
    this._readOnlyLoading = new ReadOnlySubject<boolean>(this._publisherContext.loading);
    this._readOnlyPublishing = new ReadOnlySubject<boolean>(this._publisherContext.publishing);
    this._readOnlyStandby = new ReadOnlySubject<boolean>(this._publisherContext.standby);
    this._readOnlyStopped = new ReadOnlySubject<boolean>(this._publisherContext.stopped);
    this._readOnlyFailureCount = new ReadOnlySubject<number>(this._publisherContext.failureCount);
    this._readOnlyEndPoint = new ReadOnlySubject<EndPoint | null>(this._publisherContext.endPoint);
    this._readOnlyStream = new ReadOnlySubject<IStream | null>(this._publisherContext.stream);
    this._readOnlyRtcStatistics = new ReadOnlySubject<IRtcPublishMonitorStatistic | null>(this._publisherContext.rtcStatistics);
    this._readOnlyMediaStream = new ReadOnlySubject<MediaStream | null>(this._peerConnectionContext.mediaStream);

    this._peerConnectionContext.mediaStream.value = mediaStream;

    const edgeToken = EdgeAuthParser.parseToken(this._tokenContext.token.value);
    const discoveryUri = (edgeToken.uri || SDK.discoveryUri.value).toString();

    SDK.tenancy.value = edgeToken.tenancy || SDK.tenancy.value;
    DiscoveryUri.uri.value = discoveryUri;
    this._metricsService = MetricsFactory.getMetricsService(discoveryUri);
    this._sessionTelemetry = new SessionTelemetry(SDK.pageLoadTime, this._metricsService);
    this._publisherContext.publisherDisposables.add(this._sessionTelemetry);

    this._publisherContext.publisherDisposables.add(
      this._publisherContext.state.subscribe(state => {
        if (this._publisherContext.clearFailureCountTimeout) {
          GlobalScope.value.clearTimeout(this._publisherContext.clearFailureCountTimeout);
        }

        if (!this._publisherContext.failureCount.value) {
          return;
        }

        if (state !== PublisherState.Publishing) {
          return;
        }

        this._publisherContext.clearFailureCountTimeout = GlobalScope.value.setTimeout(() => {
          this._publisherContext.failureCount.value = 0;
        }, failureCountCleanUpIntervalInMilliseconds);
      }));

    const destroyStreamOnUnmount = () => {
      if (this._publisherContext.stream.value && this._publisherContext.endPoint.value) {
        const ignored = this._publisherContext.endPoint.value.destroyStreamOnUnmount(this._publisherContext.stream.value, 'client:termination-on-window-unload');
      }
    };

    GlobalScope.value.addWindowEventListener('beforeunload', destroyStreamOnUnmount);

    this._publisherContext.publisherDisposables.add(new Disposable(() => {
      GlobalScope.value.removeWindowEventListener('beforeunload', destroyStreamOnUnmount);
    }));

    this.start();
  }

  get token(): EncodedEdgeToken {
    return this._tokenContext.token.value;
  }

  set token(token: EncodedEdgeToken) {
    this._publisherContext.disposables.dispose();

    this._tokenContext.token.value = token;
    this._tokenContext.tokenExpiring.value = false;

    const edgeToken = EdgeAuthParser.parseToken(this._tokenContext.token.value);
    const discoveryUri = (edgeToken.uri || SDK.discoveryUri.value).toString();

    SDK.tenancy.value = edgeToken.tenancy || SDK.tenancy.value;
    DiscoveryUri.uri.value = discoveryUri;

    this._metricsService = MetricsFactory.getMetricsService(discoveryUri);

    this.start();
  }

  get peerConnection(): ReadOnlySubject<IPeerConnection | null> {
    return this._readOnlyPeerConnection;
  }

  get state(): ReadOnlySubject<PublisherState> {
    return this._readOnlyState;
  }

  get tokenExpiring(): ReadOnlySubject<boolean> {
    return this._readOnlyTokenExpiring;
  }

  get authorized(): ReadOnlySubject<boolean> {
    return this._readOnlyAuthorized;
  }

  get online(): ReadOnlySubject<boolean> {
    return this._readOnlyOnline;
  }

  get loading(): ReadOnlySubject<boolean> {
    return this._readOnlyLoading;
  }

  get publishing(): ReadOnlySubject<boolean> {
    return this._readOnlyPublishing;
  }

  get standby(): ReadOnlySubject<boolean> {
    return this._readOnlyStandby;
  }

  get stopped(): ReadOnlySubject<boolean> {
    return this._readOnlyStopped;
  }

  get failureCount(): ReadOnlySubject<number> {
    return this._readOnlyFailureCount;
  }

  get endPoint(): ReadOnlySubject<EndPoint | null> {
    return this._readOnlyEndPoint;
  }

  get stream(): ReadOnlySubject<IStream | null> {
    return this._readOnlyStream;
  }

  get streamId(): string {
    return this._publisherContext.streamId;
  }

  get rtcStatistics(): ReadOnlySubject<IRtcPublishMonitorStatistic | null> {
    return this._readOnlyRtcStatistics;
  }

  get mediaStream(): ReadOnlySubject<MediaStream | null> {
    return this._readOnlyMediaStream;
  }

  async stop(reason: string): Promise<void> {
    return new Promise(resolve => {
      if (!this._stateContext.isStarting.value) {
        this.processStop(reason);

        resolve();

        return;
      }

      this._publisherContext.disposables.add(this._stateContext.isStarting.subscribe(isStarting => {
        if (!isStarting) {
          this.processStop(reason);
          resolve();
        }
      }));
    });
  }

  private processStop(reason: string): void {
    this.cleanUpResources(reason);

    if (this._peerConnectionContext.peerConnection.value) {
      this._peerConnectionContext.peerConnection.value.close();
      this._peerConnectionContext.peerConnection.value = null;
    }

    this._publisherContext.state.value = PublisherState.Stopped;
  }

  async dispose(): Promise<void> {
    return this.stop('client:channel-dispose').then(() => {
      this._publisherContext.publisherDisposables.dispose();
      this._stateContext.isDisposed = true;
    });
  }

  getUri(token: EncodedEdgeToken): URL {
    const url = EdgeAuthParser.parseToken(token).uri;

    if (url) {
      return url;
    }

    this._logger.info('Fall back to the default discover URI [%s]', SDK.discoveryUri.value);

    return new GlobalScope.value.URL(SDK.discoveryUri.value);
  }

  async start(): Promise<void> {
    if (this._stateContext.isDisposed) {
      throw new Error('Channel was already disposed');
    }

    if (this._stateContext.isStarting.value) {
      this._logger.info('Publisher is already starting, skipping start');

      return;
    }

    this._stateContext.isStarting.value = true;

    return this.processStart();
  }

  private async processStart(): Promise<void> {
    this._publisherContext.state.value = PublisherState.Starting;
    this.cleanUpResources('client:start');

    const token: EncodedEdgeToken = this._tokenContext.token.value;
    const listenOnStreamSetup = this._sessionTelemetry.listenOnStreamSetup();

    if (!EdgeAuthParser.isEncodedEdgeTokenValid(token)) {
      this._logger.error('Failed to parse token [%s]', token);
      this._publisherContext.state.value = PublisherState.Unauthorized;
      this._publisherContext.authorized.value = false;
      this._stateContext.isStarting.value = false;

      return;
    }

    this._publisherContext.loading.value = true;

    const uri = this.getUri(token);

    this._publisherContext.disposables.add(
      this._publisherContext.state.subscribe(state => {
        if (state === PublisherState.Error || state === PublisherState.Recovering) {
          Discovery.clearCachedClosestEndpoint(uri);
        }
      })
    );

    const handleStreamFailureCallback: () => Promise<void> = () => new Promise((resolve): void => {
      // Need to set isStarting to false and call handleStreamFailure if stream monitors found an issue
      this._stateContext.isStarting.value = false;

      return resolve(this.handleStreamFailure());
    });
    const streamPublisher = new RealTimePublisher(this._publisherContext, this._peerConnectionContext, handleStreamFailureCallback);

    return streamPublisher.start(
      uri,
      token,
      listenOnStreamSetup)
      .then(() => {
        this._publisherContext.loading.value = false;
      })
      .catch(e => {
        listenOnStreamSetup.fail();

        this._publisherContext.failureCount.value++;

        this._publisherContext.online.value = false;

        this.cleanUpResources('client:cleanup-after-failed-setup');

        this._publisherContext.state.value = PublisherState.ClientStartError;

        this._logger.error('Failed to start publishing', e);
      })
      .finally(() => {
        this._stateContext.isStarting.value = false;

        if (this._publisherContext.state.value === PublisherState.Publishing || !SDK.automaticRetryOnFailure) {
          this._publisherContext.startReconnectionAttemptsTimeInMilliseconds = 0;

          return;
        }

        const timeoutId = GlobalScope.value.setTimeout(() => {
          const ignored = this.handleStreamFailure()
            .catch(e => {
              this._logger.error('Failed handling stream failure', e);
            });
        }, this.getRetryInterval());

        this._publisherContext.disposables.add(new Disposable(() => {
          GlobalScope.value.clearTimeout(timeoutId);
        }));
      });
  }

  setMediaStream(mediaStream: MediaStream) {
    this._peerConnectionContext.mediaStream.value = mediaStream;

    if (this._peerConnectionContext.peerConnection.value) {
      mediaStream.getTracks().forEach(mediaTrack => {
        this._peerConnectionContext.peerConnection.value?.getSenders().forEach(sender => {
          if (mediaTrack.kind === sender?.track?.kind && mediaTrack.id !== sender.track.id) {
            sender.replaceTrack(mediaTrack);
          }
        });
      });
    }
  }

  private getRetryInterval(): number {
    switch (this._publisherContext.state.value) {
      case PublisherState.Offline:
        return standbyPollingIntervalInMilliseconds;
      case PublisherState.Initializing:
      case PublisherState.NotFound:
      case PublisherState.Error:
      case PublisherState.Recovering:
      case PublisherState.Unauthorized:
      case PublisherState.GeoRestricted:
      case PublisherState.GeoBlocked:
      case PublisherState.Stopped:
      case PublisherState.Starting:
      case PublisherState.Publishing:
      case PublisherState.Reconnecting:
      case PublisherState.UnsupportedFeature:
      case PublisherState.ConfigurationError:
      case PublisherState.TransientConfigurationError:
      case PublisherState.ConnectionError:
      case PublisherState.ClientStartError:
        // First and second attempt fast, after that exponential with backoff interval
        return this._exponentialBackoff.getExponentialBackoffIntervalByFailureCount(this._publisherContext.failureCount.value);

      default:
        assertUnreachable(this._publisherContext.state.value);
    }
  }

  private async handleStreamFailure(): Promise<void> {
    let reconnectingAttemptsInterval = 0;

    switch (this._publisherContext.state.value) {
      case PublisherState.Error:
      case PublisherState.Reconnecting:
      case PublisherState.Offline:
      case PublisherState.Recovering:
      case PublisherState.TransientConfigurationError:
      case PublisherState.ConnectionError:
      case PublisherState.ClientStartError:
        if (!this._publisherContext.startReconnectionAttemptsTimeInMilliseconds) {
          this._publisherContext.startReconnectionAttemptsTimeInMilliseconds = Date.now();
        }

        reconnectingAttemptsInterval = Date.now() - this._publisherContext.startReconnectionAttemptsTimeInMilliseconds;

        if (maxReconnectingAttemptsIntervalInMilliseconds <= reconnectingAttemptsInterval) {
          this._logger.info('Publisher stopped with state [%s] [%s] after exceeding timeout', this._publisherContext.state.value, PublisherState[this._publisherContext.state.value]);

          return;
        }

        this._logger.info('Retry start with initial state [%s] [%s]', this._publisherContext.state.value, PublisherState[this._publisherContext.state.value]);

        break;
      case PublisherState.Unauthorized:
        this._logger.info('Publisher is unauthorized, skipping retry of start. Please provide a new token and invoke start()');

        return;
      case PublisherState.GeoRestricted:
        this._logger.info('Publisher is geo restricted, skipping retry of start. Please provide a new token and invoke start()');

        return;
      case PublisherState.GeoBlocked:
        this._logger.info('Publisher is geo blocked, skipping retry of start. Please provide a new token and invoke start()');

        return;
      case PublisherState.Stopped:
        this._logger.info('Publisher is stopped, skipping retry of start.');

        return;
      case PublisherState.Publishing:
        this._logger.info('Publisher is publishing, skipping retry of start');

        return;
      case PublisherState.Initializing:
        this._logger.info('Publisher is initializing, skipping retry of start');

        return;
      case PublisherState.Starting:
        this._logger.info('Publisher is already starting, skipping retry of start');

        return;
      case PublisherState.UnsupportedFeature:
        this._logger.info('Publisher is stopped due to unsupported feature, skipping retry of start.');

        return;
      case PublisherState.NotFound:
        this._logger.info('Publisher is stopped due to channel not found, skipping retry of start.');

        return;
      case PublisherState.ConfigurationError:
        this._logger.info('Publisher is stopped due to configuration error, skipping retry of start.');

        return;
      default:
        assertUnreachable(this._publisherContext.state.value);
    }

    return this.start();
  }

  private cleanUpResources(reason: string = defaultStreamTerminationReason): void {
    this._publisherContext.disposables.dispose();

    const peerConnection = this._peerConnectionContext.peerConnection.value;

    if (peerConnection) {
      this._peerConnectionContext.peerConnection.value = null;
      peerConnection.close();
      peerConnection.dispose();

      if (SDK.forceGarbageCollectionOnRestart && !(FeatureEnablement.isMobile && SDK.skipGarbageCollectionOnMobileDevices)) {
        GarbageCollectorManager.forceGarbageCollection();
      }
    }

    this._publisherContext.publishing.value = false;
    this._publisherContext.stopped.value = true;
    this._publisherContext.standby.value = false;

    if (this._publisherContext.stream.value && this._publisherContext.endPoint.value) {
      const ignored = this._publisherContext.endPoint.value.destroyStream(this._publisherContext.stream.value, reason)
        .then(({status}) => {
          if (status !== 'ok') {
            this._logger.warn('[%s] Failed to destroy stream with reason [%s]', this.streamId, status);

            return;
          }

          this._logger.info('[%s] Destroyed stream with reason [%s]', this.streamId, status);
        })
        .catch(e => {
          this._logger.error('[%s] Failed to destroy stream', this.streamId, e);
        });
    }

    this._publisherContext.stream.value = null;
    this._publisherContext.endPoint.value = null;
    this._peerConnectionContext.peerConnectionReconnectAttempts = 0;
  }
}